import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {ApiService} from '../../../@core/service/api.service';
import {NbToastrService} from '@nebular/theme';
import {AuthGuardService} from '../../../@core/service/auth-guard.service';
import {SharedDataService} from '../../../@core/service/shared-data.service';
import {BaseProductModel} from '../../../@models/erp/baseProduct';
import {
  BusinessModel,
  EProjectDevelopmentDisplay,
  EProjectQuantityUnit,
  EProjectQuantityUnitDisplay,
  EProjectStatus,
  EProjectType,
  MaterialModel,
  ProjectModel,
  UserModel,
} from '../../../@models';
import {CompilationModel} from '../../../@models/erp/compilation';
import {FormListDateRendererComponent} from '../form-list/renderer/form-list-date-renderer.component';
import {FormListDateEditComponent} from '../form-list/renderer/form-list-date-edit.component';
import moment from 'moment';

@Component({
  selector: 'ngx-project-edit',
  templateUrl: './project-edit.component.html',
  styleUrls: ['./project-edit.component.scss'],
})
export class ProjectEditComponent implements OnInit, OnDestroy {
  @Input() project: ProjectModel;
  @Input() customer: BusinessModel;
  @Output() saved = new EventEmitter<ProjectModel>();
  @Output() cancelled = new EventEmitter<void>();

  me: UserModel;
  offers: (Partial<CompilationModel> & { product: string, totalNet: string })[] = [];

  businessUnits: { id: string; name: string }[] = [];
  materials: MaterialModel[] = [];
  materialOptions = this.data.materialOptions;
  baseProducts: BaseProductModel[] = [];
  baseProductOptions: { id: string; name: string }[] = [];
  currencies = [{id: 'EUR', name: 'EUR'}, {id: 'USD', name: 'USD'}, {id: 'JPY', name: 'JPY'}];
  unitOptions = EProjectQuantityUnitDisplay;
  developmentOptions = EProjectDevelopmentDisplay;
  ProjectQuantityUnit = EProjectQuantityUnit;
  projectTypeMap: { id: number; name: string }[] = [];
  projectStatusMap: { id: number; name: string }[] = [];

  milestoneColumns = {
    name: {
      title: 'Name',
      filter: false,
    },
    date: {
      title: 'Due date',
      type: 'custom',
      filter: false,
      renderComponent: FormListDateRendererComponent,
      editor: {
        type: 'custom',
        component: FormListDateEditComponent,
      },
    },
    completion: {
      title: 'Completion %',
      type: 'text',
      filter: false,
      editor: {
        type: 'list',
        config: {
          list: [
            {value: '0', title: '  0 %'},
            {value: '0.1', title: ' 10 %'},
            {value: '0.2', title: ' 20 %'},
            {value: '0.3', title: ' 30 %'},
            {value: '0.4', title: ' 40 %'},
            {value: '0.5', title: ' 50 %'},
            {value: '0.6', title: ' 60 %'},
            {value: '0.7', title: ' 70 %'},
            {value: '0.8', title: ' 80 %'},
            {value: '0.9', title: ' 90 %'},
            {value: '1', title: '100 %'},
          ],
        },
      },
    },
    text: {
      filter: false,
      title: 'Text',
    },
  };

  isSaving = false;

  private business: BusinessModel[];
  private subscriptions: Subscription[] = [];
  private errorSubscription: Subscription;

  constructor(
    private api: ApiService,
    private auth: AuthGuardService,
    private data: SharedDataService,
    private toastrService: NbToastrService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.auth.loadMe().then(x => this.me = x);

    for (const n in EProjectType) {
      if (typeof EProjectType[n] === 'number') {
        this.projectTypeMap.push({id: <any>EProjectType[n], name: n});
      }
    }

    for (const n in EProjectStatus) {
      if (typeof EProjectStatus[n] === 'number') {
        this.projectStatusMap.push({id: <any>EProjectStatus[n], name: n});
      }
    }

    this.subscriptions.push(
      this.api.reloadBusinessUnits().subscribe(bu => {
        this.businessUnits = bu.map(b => ({ id: b._id, name: b.name }));
      }),
    );

    this.subscriptions.push(
      this.api.reloadMaterials().subscribe(mat => {
        this.materials = mat;
      }),
    );

    this.subscriptions.push(
      this.api.reloadBusiness().subscribe(b => {
        this.business = b;
        if (this.project?._id && !this.customer) {
          this.loadCustomer(this.project._id);
        }
      }),
    );

    this.errorSubscription = this.api.error.subscribe(() => {
      this.isSaving = false;
    });

    await this.data.loadProducts();
    this.baseProducts = this.data.baseProducts;
    this.baseProductOptions = [...new Set(this.baseProducts
      .sort((a, b) => a.name > b.name ? 1 : -1)
      .map(m => ({ id: m.code, name: `${m.code} ' | ' ${m.trial} ' | ' ${m.name}` })))];

    if (this.project) {
      this.filterMaterial(this.project.businessUnit?.toString());
      if (this.project.unit == null) this.project.unit = EProjectQuantityUnit.Disabled;
      if (this.project.quantity == null) this.project.quantity = 0;
      if (this.project.price == null) this.project.price = 0;
      if (this.project.offers == null) this.project.offers = [];
      if (this.project.milestones == null || this.project.milestones.length === 0) {
        this.project.milestones = [
          {
            completion: 0,
            name: 'Completion',
          },
        ];
      }
      this.loadOffers();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.errorSubscription) this.errorSubscription.unsubscribe();
  }

  loadCustomer(_id: string): void {
    this.customer = this.business?.find(x => x.projects.find(y => y._id === _id) != null);
  }

  filterMaterial(buId: string): void {
    // this.materialOptions =
    //   [{id: null, name: 'None'}].concat(this.materials.filter(x => x.businessUnit as string === buId)
    //     .map(m => ({ id: m._id, name: m.name })));
    this.baseProductOptions = [...new Set(this.baseProducts
      .filter(f => f.businessUnit === this.project?.businessUnit)
      .sort((a, b) => a.name > b.name ? 1 : -1)
      .map(m => ({ id: m.code, name: `${m.code} | [${m.trial}] | ${m.name}` })))];
  }

  async addOffer(offerCode: string): Promise<void> {
    if (this.offers?.map(m => m.code)?.includes(offerCode)) return;
    const ofnrka = await this.api.loadCompilationByCode(offerCode);
    this.offers.push({
      ...ofnrka,
      product: ofnrka.lineItems[0]?.finishedProduct?.calcName,
      totalNet: this.data.calcData(ofnrka).totalNet,
    });
    this.project.offers.push(ofnrka.code);
  }

  removeOffer(idx: number): void {
    this.project.offers.splice(idx, 1);
    this.offers.splice(idx, 1);
  }

  async loadOffers(): Promise<void> {
    this.offers = [];
    for (const offerCode of this.project.offers || []) {
      await this.addOffer(offerCode);
    }
    if (!this.project.offers?.length && this.project.offer) await this.addOffer(this.project.offer);
  }

  arrangeOffers = () => this.offers?.sort((a, b) => moment(b.issueDate).diff(moment(a.issueDate)))

  onOfSelected(event: any): void {
    this.addOffer(event.code);
  }

  changeMilestones($event: any[]): void {
  }

  validateMilestones(event: any): void {
    event.confirm.resolve();
  }

  save(): void {
    if (this.customer == null) {
      this.toastrService.danger('Customer is required', 'Error');
    } else if (this.project.businessUnit == null) {
      this.toastrService.danger('BU is required', 'Error');
    } else if (this.project.name == null || this.project.name.length === 0) {
      this.toastrService.danger('Name is required', 'Error');
    } else if (this.project.type == null) {
      this.toastrService.danger('Type is required', 'Error');
    } else if (this.project.status == null) {
      this.toastrService.danger('Status is required', 'Error');
    } else {
      if (this.project.material === 'null') this.project.material = null;

      this.isSaving = true;
      if (this.project._id == null) {
        const newProjectCustomer = JSON.parse(JSON.stringify(this.customer));
        newProjectCustomer.projects.push(this.project);
        this.api.saveBusiness(newProjectCustomer).subscribe(() => {
          this.toastrService.success(`Project '${this.project.name}' saved!`, 'Success');
          this.isSaving = false;
          this.saved.emit(this.project);
        });
      } else {
        this.api.saveProject(this.project).subscribe(() => {
          this.toastrService.success(`Project '${this.project.name}' saved!`, 'Success');
          this.isSaving = false;
          this.saved.emit(this.project);
        });
      }
    }
  }

  cancel(): void {
    this.cancelled.emit();
  }
}